declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

const GA_MEASUREMENT_ID =
  (typeof window !== 'undefined' &&
    (window as { env?: { REACT_APP_GA_MEASUREMENT_ID?: string } }).env
      ?.REACT_APP_GA_MEASUREMENT_ID) ||
  '';

const gtag = (...args: unknown[]) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
};

interface AnalyticsEvent {
  category?: string;
  label?: string;
  value?: number;
  [key: string]: unknown;
}

class GoogleAnalytics {
  private isInitialized = false;

  constructor() {
    this.init();
  }

  private init(): void {
    if (this.isInitialized) return;

    // Without a measurement id (window.env), GA is disabled: leave
    // isInitialized false so all track methods stay no-ops.
    if (!GA_MEASUREMENT_ID) return;

    try {
      gtag('js', new Date());
      gtag('config', GA_MEASUREMENT_ID, {
        send_page_view: false,
        cookie_flags: 'SameSite=None;Secure',
      });

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
    }
  }

  trackPageView(path: string, title: string | null = null): void {
    if (!this.isInitialized) return;

    try {
      gtag('config', GA_MEASUREMENT_ID, {
        page_path: path,
        page_title: title || document.title,
      });
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  trackEvent(action: string, parameters: AnalyticsEvent = {}): void {
    if (!this.isInitialized) return;

    try {
      gtag('event', action, {
        event_category: parameters.category || 'engagement',
        event_label: parameters.label || '',
        value: parameters.value || 0,
        ...parameters,
      });
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  trackLogin(method: string = 'email'): void {
    this.trackEvent('login', {
      method: method,
      event_category: 'authentication',
    });
  }

  trackLogout(): void {
    this.trackEvent('logout', {
      event_category: 'authentication',
    });
  }

  trackSearch(searchTerm: string, category: string = 'general'): void {
    this.trackEvent('search', {
      search_term: searchTerm,
      event_category: category,
    });
  }

  trackRTLView(rtlId: string): void {
    this.trackEvent('rtl_view', {
      event_category: 'rtl',
      event_label: rtlId,
      rtl_id: rtlId,
    });
  }

  trackRTLWarning(rtlId: string, warningType: string): void {
    this.trackEvent('rtl_warning', {
      event_category: 'rtl',
      event_label: `${rtlId}_${warningType}`,
      rtl_id: rtlId,
      warning_type: warningType,
    });
  }

  trackRTLToleranceProfile(rtlId: string, profileAction: string): void {
    this.trackEvent('rtl_tolerance_profile', {
      event_category: 'rtl',
      event_label: `${rtlId}_${profileAction}`,
      rtl_id: rtlId,
      profile_action: profileAction,
    });
  }

  trackShipmentView(shipmentId: string): void {
    this.trackEvent('shipment_view', {
      event_category: 'shipments',
      event_label: shipmentId,
      shipment_id: shipmentId,
    });
  }

  trackLocationView(locationId: string): void {
    this.trackEvent('location_view', {
      event_category: 'locations',
      event_label: locationId,
      location_id: locationId,
    });
  }

  trackUserManagement(action: string, userId: string | null = null): void {
    this.trackEvent('user_management', {
      event_category: 'administration',
      event_label: action,
      user_action: action,
      target_user_id: userId,
    });
  }

  trackAdminAction(
    action: string,
    details: Record<string, unknown> = {}
  ): void {
    this.trackEvent('admin_action', {
      event_category: 'administration',
      event_label: action,
      admin_action: action,
      ...details,
    });
  }

  trackSupport(action: string, topic: string | null = null): void {
    this.trackEvent('support', {
      event_category: 'help',
      event_label: action,
      support_action: action,
      help_topic: topic,
    });
  }

  trackDownload(fileName: string, fileType: string): void {
    this.trackEvent('file_download', {
      event_category: 'engagement',
      event_label: fileName,
      file_name: fileName,
      file_type: fileType,
    });
  }

  trackFormSubmission(formName: string, success: boolean = true): void {
    this.trackEvent('form_submit', {
      event_category: 'engagement',
      event_label: formName,
      form_name: formName,
      success: success,
    });
  }

  trackError(
    errorType: string,
    errorMessage: string,
    page: string | null = null
  ): void {
    this.trackEvent('exception', {
      description: errorMessage,
      fatal: false,
      error_type: errorType,
      page:
        page || (typeof window !== 'undefined' ? window.location.pathname : ''),
    });
  }

  setUserProperties(
    userId: string,
    userRole: string,
    isAdmin: boolean = false
  ): void {
    if (!this.isInitialized) return;

    try {
      gtag('config', GA_MEASUREMENT_ID, {
        user_id: userId,
        custom_map: {
          user_role: userRole,
          is_admin: isAdmin,
        },
      });

      gtag('set', {
        user_role: userRole,
        is_admin: isAdmin,
      });
    } catch (error) {
      console.error('Failed to set user properties:', error);
    }
  }

  clearUserData(): void {
    if (!this.isInitialized) return;

    try {
      gtag('config', GA_MEASUREMENT_ID, {
        user_id: null,
      });
    } catch (error) {
      console.error('Failed to clear user data:', error);
    }
  }
}

const analytics = new GoogleAnalytics();

export default analytics;
